function filterNAValues(items: any[]): any[] {
    return items.filter((item) => item.value !== 'N/A');
}

function formatCurrency(value: any) {
    const numValue = parseFloat(value);
    const safeValue = isNaN(numValue) ? 0 : numValue;
    return `$${safeValue.toLocaleString('en-US')}`;
}

function formatDate(value: string): string {
    if (!value) return value;

    // Try to parse the date
    const date = new Date(value);

    // Check if date is valid
    if (isNaN(date.getTime())) {
        // If not a valid date, just replace hyphens with slashes
        return value.replace(/-/g, '/');
    }

    // Format as mm/dd/yyyy
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();

    return `${month}/${day}/${year}`;
}

export function mapPolicyGeneralInformation(data: any) {
    const fieldList = filterNAValues(
        Object.values(data).map((item: any) => ({
            type: item.node === 'Status' ? 'cell_row_tag_value' : 'cell_row_cap_value',
            caption: item.node,
            value: ['Annual premium', 'Monthly premium'].includes(item.node)
                ? formatCurrency(item.value)
                : ['Effective date', 'Expiration date'].includes(item.node)
                ? formatDate(item.value)
                : item.value,
            tagTheme:
                item.node === 'Status'
                    ? item.value?.toLowerCase() === 'inforce'
                        ? 'success'
                        : item.value?.toLowerCase() === 'cancelled'
                        ? 'default'
                        : item.value?.toLowerCase() === 'expired'
                        ? 'error'
                        : 'default'
                    : undefined,
            helpText: item.helpText,
        }))
    );

    return {
        name: 'Policy Snapshot',
        options: {
            optionsList:
                fieldList.length > 0
                    ? [
                          {
                              fieldName: 'Policy general information',
                              iconPath: 'policy-status',
                              fieldList,
                          },
                      ]
                    : [],
        },
    };
}

export function mapBillingInformation(data: any[]) {
    const safe = (obj: any, path: string) => path.split('.').reduce((o, k) => o?.[k], obj);

    const items = data.length > 0 ? data : [{}]; // fallback to one empty item

    const optionsList = items.map((item, index) => {
        const billingType = safe(item, 'billingType.value');
        const enrolled = safe(item, 'enrolledInAutoPayments.value');
        const cardBrand = safe(item, 'cardBrand.value');
        const cardNumber = safe(item, 'cardNumber.value');
        const nameOnCard = safe(item, 'nameOnCard.value');
        const expiry = safe(item, 'expiry.value');
        const hasAllCardFields = cardBrand && cardNumber && nameOnCard && expiry;

        const fieldList = [
            { type: 'cell_row_cap_value', caption: 'Billing type', value: billingType ?? '' },
            ...(hasAllCardFields
                ? [
                      {
                          type: 'cell_row_description_img_value',
                          iconPath: cardBrand.toLowerCase(),
                          caption: `${cardBrand} ${cardNumber}`,
                          description: `<p class='caption'>${nameOnCard} <br> Expires in ${expiry}</p>`,
                      },
                  ]
                : []),
            { type: 'cell_row_cap_value', caption: 'Enrolled in auto-payments', value: enrolled ?? '' },
        ];

        return {
            fieldName: index === 0 ? 'Payment method' : undefined,
            iconPath: index === 0 ? 'payments-icon' : undefined,
            fieldList,
        };
    });

    return {
        name: 'Billing information',
        options: { optionsList },
    };
}

export function mapPaymentInformation(data: any) {
    const optionsList = [
        mapCurrentStatement(data?.currentStatement),
        mapPaymentFrequency(data?.paymentFrequency),
        mapPaymentHistory(data?.paymentHistory),
        mapRefund(),
    ].filter((option) => option.fieldList.length > 0);

    return {
        name: 'Payment information',
        options: {
            optionsList,
        },
    };
}

export function mapCurrentStatement(data: any) {
    const fieldList = filterNAValues([
        {
            type: 'cell_row_bold_description_value',
            caption: `${data?.dueDate.node} ${data?.dueDate.value}`,
            value: `$${data?.amount.value ? data?.amount.value : '0.00'}`,
            description: `<p class='caption text-end'>Outstanding balance <span class='disclaimer-bold'>$${
                data?.amount.value ? data?.amount.value : '0.00'
            }</span></p>`,
        },
        {
            type: 'cell_row_cap_value',
            caption: data?.invoiceNumber.node,
            value: data?.invoiceNumber.value,
        },
    ]);

    return {
        fieldName: 'Current statement',
        iconPath: 'quote-icon',
        fieldList,
    };
}

export function mapPaymentFrequency(data: any) {
    const fieldList = filterNAValues([
        { type: 'cell_row_cap_value', caption: data?.numberOfPayments.node, value: data?.numberOfPayments.value },
        {
            type: 'cell_row_cap_value',
            caption: 'Schedule',
            value: `${data?.schedule.length} payment(s) over next ${data?.schedule.length} month(s)`,
        },
        ...data?.schedule.map((s: any) => ({
            type: 'table_row_cap_value',
            caption: s.dueDate.value,
            value: `$${s.amount.value}`,
        })),
    ]);

    return {
        fieldName: 'Payment frequency',
        iconPath: 'payment-frequency',
        collapse: true,
        isOpen: false,
        fieldList,
    };
}

export function mapPaymentHistory(data: any[]) {
    const fieldList = filterNAValues(
        (data ?? []).map((record: any) => {
            const date = formatDate(record?.date?.value);
            const amount = formatCurrency(record?.amount?.value);
            const method = record?.method?.value ?? '';
            const cardNumber = record?.cardNumber?.value ?? '';
            const cardType = record?.cardType?.value ?? '';

            const description =
                cardType && cardNumber
                    ? `<p class='text-end caption'>${cardType} ****${cardNumber.slice(-4)}</p>`
                    : `<p class='text-end caption'>${method}</p>`;

            return {
                type: 'cell_row_description_value',
                caption: date,
                value: amount,
                description,
            };
        })
    );

    return {
        fieldName: 'Payment history',
        iconPath: 'payment-history',
        collapse: true,
        isOpen: false,
        fieldList,
    };
}

export function mapInsuredInformation(data: any) {
    const safeObjectValues = (v: any) => (v && typeof v === 'object' ? Object.values(v) : []);

    const optionsList = ['owner', 'additionalInsured']
        .map((key, index) => {
            const itemList = safeObjectValues(data[key]); // if key is missing → returns []

            const fieldList = filterNAValues(
                itemList
                    .filter((item: any) => item?.node && item?.value) // only valid rows
                    .map((item: any) => ({
                        type: 'cell_row_cap_value',
                        caption: item.node,
                        value: item.value,
                    }))
            );

            return {
                fieldName: index === 0 ? 'Owner' : 'Additional insured',
                iconPath: index === 0 ? 'user6' : 'user1',
                fieldList,
            };
        })
        .filter((option) => option.fieldList.length > 0); // remove entire section if empty OR missing

    return {
        name: 'Insured information',
        options: { optionsList },
    };
}

export function mapContactPreferences(data: any) {
    if (!data) return null;

    const enrolled = {
        type: 'cell_row_cap_value',
        caption: data?.enrolledInEDelivery?.node ?? 'Enrolled in e-delivery',
        value: data?.enrolledInEDelivery?.value ?? '-',
    };

    const mailing = {
        type: 'cell_row_cap_value',
        caption: data?.mailingAddress?.node ?? 'Mailing address',
        value: data?.mailingAddress?.value ?? '-',
    };

    const mergedContacts: any[] = [];

    const emails = data?.contactInfo?.emails ?? [];
    const phones = data?.contactInfo?.phones ?? [];
    const names = data?.contactInfo?.name ?? [];

    const maxLength = Math.max(emails.length, phones.length, names.length);

    for (let i = 0; i < maxLength; i++) {
        const normalize = (val) => {
            if (!val) return '';
            const v = String(val).trim().toLowerCase();
            return v === 'na' || v === 'null' || v === 'undefined' ? '' : String(val).trim();
        };

        const emailItem = normalize(emails[i]?.email?.value);
        const phoneItem = normalize(phones[i]?.phone?.value);
        const nameItem = normalize(names[i]?.name?.value);

        const caption = nameItem;
        const value = `${emailItem}${emailItem && phoneItem ? ' | ' : ''}${phoneItem}`;

        if (caption || value) {
            mergedContacts.push({
                type: 'cell_row_cap_value',
                caption,
                value,
            });
        }
    }

    const fieldList = filterNAValues([enrolled, mailing, ...mergedContacts]);

    return {
        name: 'Contact preferences',
        options: {
            optionsList:
                fieldList.length > 0
                    ? [
                          {
                              fieldName: 'Contact info',
                              iconPath: 'contact-information',
                              fieldList,
                          },
                      ]
                    : [],
        },
    };
}

export function mapThirdPartyInterest(data: any) {
    const optionsList = Object.entries(data)
        .map(([key, obj]) => {
            const fieldList = filterNAValues(
                Object.values(obj as any).map((v: any) => ({
                    type: 'cell_row_cap_value',
                    caption: v.node,
                    value: v.value,
                }))
            );
            return {
                fieldName: key === 'additionalInterest' ? 'Additional interest (1)' : 'Mortgage Company',
                iconPath: key === 'additionalInterest' ? 'third-party-interest' : 'llc',
                fieldList,
            };
        })
        .filter((option) => option.fieldList.length > 0);

    return {
        name: 'Third party interest',
        options: {
            optionsList,
        },
    };
}

export function mapCoverages(data: any) {
    const coverageSection = (
        fieldName: string,
        icon: string,
        obj: any,
        addDollar: boolean = false,
        isNiceToHave: boolean = false
    ) => {
        const fieldList = filterNAValues(
            Object.values(obj).map((c: any) => {
                let value = c.value;

                // Special handling for Medical Payments in Must haves - return raw value
                if (fieldName === 'Must haves' && c.node === 'Medical Payments') {
                    value = c.value;
                }
                // Special handling for $5,000 Theft Limitation in Must haves
                else if (fieldName === 'Must haves' && c.node === '$5,000 Theft Limitation') {
                    value = parseInt(c.value) === 1 ? 'Included' : 'Not included';
                }
                // Special handling for Increased Personal Computer items in Nice to have section
                else if (
                    isNiceToHave &&
                    (c.node === 'Increased Personal Computer Systems' ||
                        c.node === 'Increased Personal Computer Programs') &&
                    parseInt(c.value) === 0
                ) {
                    value = formatCurrency(c.value);
                } else if (isNiceToHave && parseInt(c.value) === 0) {
                    value = 'Not included';
                } else if (addDollar && c.value) {
                    value = formatCurrency(c.value);
                } else if (fieldName === 'Enhanced' && c.node === 'Limited Sewer' && c.value) {
                    value = formatCurrency(c.value);
                }
                return {
                    type: 'cell_row_cap_value_premium',
                    caption: c.node,
                    value: value,
                    premium: '$00.0',

                    helpText: c.helpText,
                };
            })
        );
        return {
            fieldName,
            iconPath: icon,
            fieldList,
        };
    };

    const optionsList = [
        coverageSection('Must haves', 'must-haves', data?.mustHaves, true),
        coverageSection('Enhanced', 'nice-to-haves', data?.enhanced),
        coverageSection('Nice to have', 'enhanced', data?.niceToHave, false, true),
    ].filter((option) => option.fieldList.length > 0);

    return {
        name: 'Coverages',
        options: {
            optionsList,
        },
    };
}

export function mapValuables(data: any) {
    const fieldList = filterNAValues(
        Object.values(data).map((v: any) => ({
            type: 'cell_row_cap_value_premium',
            caption: v.node,
            value: v.value == 0 ? formatCurrency(v.value) : v.value,
            premium: '$00.0',
            helpText: v.helpText,
        }))
    );

    return {
        name: 'Valuables',
        options: {
            optionsList:
                fieldList.length > 0
                    ? [
                          {
                              fieldName: 'Valuables',
                              iconPath: 'valuables',
                              fieldList,
                          },
                      ]
                    : [],
        },
    };
}

export function mapDiscounts(data: any) {
    const fieldList = filterNAValues(
        Object.values(data).map((v: any) => ({
            type: 'cell_row_cap_value_premium',
            caption: v.node,
            value: v.value,
            premium: '$00.0',
            helpText: v.helpText,
        }))
    );

    return {
        name: 'Discounts & Surcharges',
        options: {
            optionsList:
                fieldList.length > 0
                    ? [
                          {
                              fieldName: 'Discounts & Surcharges',
                              iconPath: 'discounts',
                              fieldList,
                          },
                      ]
                    : [],
        },
    };
}

export function mapSurcharges() {
    const fieldList = [
        { caption: 'Surcharge 1', premium: '$00.0' },
        { caption: 'Surcharge 2', premium: '$00.0' },
    ].map((s) => ({
        type: 'cell_row_2col_premium',
        caption: s.caption,
        premium: s.premium,
    }));

    return {
        name: 'Surcharges',
        options: {
            optionsList: [
                {
                    fieldName: 'Surcharges',
                    iconPath: 'Surcharges-icon',
                    fieldList,
                },
            ],
        },
    };
}

export function mapFees() {
    const fieldList = [
        { caption: 'Fee 1', premium: '$00.0' },
        { caption: 'Fee 2', premium: '$00.0' },
    ].map((f) => ({
        type: 'cell_row_2col_premium',
        caption: f.caption,
        premium: f.premium,
    }));

    return {
        name: 'Fees',
        options: {
            optionsList: [
                {
                    fieldName: 'Fees',
                    iconPath: 'Fees-icon',
                    fieldList,
                },
            ],
        },
    };
}

export function mapRefund() {
    const fieldList = [{ type: 'cell_row_cap_value', caption: 'Refund amount', value: '$00.0' }];

    return {
        fieldName: 'Refund',
        iconPath: 'Refund-icon',
        fieldList,
    };
}

export function mapClaims(claims: any[]) {
    const optionsList = claims.map((claim, index) => ({
        fieldName: index === 0 ? `Claims` : '',
        iconPath: index === 0 ? 'claims-icon' : null,
        fieldList: [
            {
                type: 'cell_row_cap_value',
                caption: 'Cause of Loss',
                value: claim.causeOfLoss,
                helpText: 'The cause of the loss event.',
            },
            {
                type: 'cell_row_cap_value',
                caption: 'Loss Reported Date',
                value: claim.lossReportedDate,
                helpText: 'The date the loss was reported.',
            },
            {
                type: 'cell_row_cap_value',
                caption: 'Loss Status',
                value: claim.lossStatus,
                helpText: 'Current status of the claim.',
            },
            {
                type: 'cell_row_cap_value',
                caption: 'Loss Amount',
                value: claim.lossAmount,
                helpText: 'The estimated or settled loss amount.',
            },
        ],
    }));

    return {
        name: 'Claims',
        options: { optionsList },
    };
}
